import { inject } from '@angular/core';
import { IntegrationManagerService } from '../../services/integration-manager.service';
import { Observable, catchError, exhaustMap, filter, forkJoin, map, of, shareReplay, tap, withLatestFrom } from 'rxjs';
import { FullScreenLoadingService } from 'src/app/shared/services/full-screen-loading.service';
import { Store } from '@ngrx/store';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { ConfirmationDialogSettings } from 'src/app/shared/models/confirmation-dialog-settings.model';
import { openConfirmationDialog } from 'src/app/shared/actions/confirmation-dialog.actions';
import {
    selectConfirmationDialogData,
    selectConfirmationDialogResult
} from 'src/app/shared/selectors/confirmation-dialog.selectors';
import { IdentityService } from 'src/app/core/identity.service';
import { MatDialogRef } from '@angular/material/dialog';
import { RunIntegrationComponent } from './run-integration.component';
import {
    ClientIntegrationIdentifier,
    ClientIntegrationIdentifierExt,
    ClientIntegrationIdentifierVersion
} from '../../models/client-integration-identifier';
import { ClientIntegrationType } from '../../models/integration-lookup.model';

export interface ClientIdentifiersVersionValues {
    v1?: ClientIntegrationIdentifier[];
    v2?: ClientIntegrationType;
}

export const getIntegrationIdentifiers = (): (() => Observable<ClientIdentifiersVersionValues>) => {
    const managerService = inject(IntegrationManagerService);

    return () => {
        const v2$ = managerService.getIntegrationMappingsLookup().pipe(
            map((x) => x.clientIntegrationType),
            catchError(() => of({}))
        );

        const v1$ = managerService.getIntegrationV1Metadata().pipe(catchError(() => of([])));

        return forkJoin({ v1$, v2$ }).pipe(
            map((x) => ({
                v1: x.v1$,
                v2: x.v2$
            })),
            shareReplay({
                bufferSize: 1,
                refCount: true
            })
        );
    };
};

export const getConfirmationDialogSettings = (identifier: ClientIntegrationIdentifierExt): ConfirmationDialogSettings =>
    ({
        title: 'Confirm',
        message: `Are you sure you want to run this integration \
    (${identifier.clientName} => ${identifier.integrationType} - ${identifier.version})? This cannot be undone.`,
        action: 'Yes, Run',
        cancelText: 'Cancel',
        addtlData: identifier
    }) as ConfirmationDialogSettings;

export const showConfirmationDialog = (): ((identifier: ClientIntegrationIdentifierExt) => void) => {
    const store = inject(Store);

    return (identifier: ClientIntegrationIdentifierExt) => {
        const settings = getConfirmationDialogSettings(identifier);
        return store.dispatch(openConfirmationDialog({ settings }));
    };
};

export const selectIntegrationRun = (): Observable<boolean> => {
    const store = inject(Store);
    const managerService = inject(IntegrationManagerService);
    const loader = inject(FullScreenLoadingService);
    const dialogRef = inject(MatDialogRef<RunIntegrationComponent>);

    return store.select(selectConfirmationDialogResult).pipe(
        filter((result) => result),
        withLatestFrom(store.select(selectConfirmationDialogData)),
        exhaustMap(([result, identifier]) => {
            loader.show();
            const runIntegration$ =
                (identifier as ClientIntegrationIdentifierExt).version === ClientIntegrationIdentifierVersion.V1
                    ? managerService.runV1Integration(identifier as ClientIntegrationIdentifier)
                    : managerService.runV2Integration(identifier as ClientIntegrationIdentifier);

            return runIntegration$.pipe(
                tap((value) => {
                    loader.hide();
                    if (value) {
                        dialogRef.close(true);
                    } else {
                        store.dispatch(
                            showToasterMessage({
                                message: 'Error running integration',
                                duration: 2000
                            })
                        );
                    }
                })
            );
        })
    );
};

export const hasAccessToRunIntegration = (): boolean => {
    const identity = inject(IdentityService);
    return identity.hasPermission('admin', 'Integration-runfromdashboard');
};

export const getClientNames = (identifier: ClientIdentifiersVersionValues): string[] => {
    const v2ClientNames = [...Object.keys(identifier?.v2 ?? {})];
    const v1ClientNames = identifier?.v1.map((x) => x.clientName) ?? [];
    return [...new Set([...v1ClientNames, ...v2ClientNames])].sort((a, b) => a.localeCompare(b));
};

export const RunIntegrationUtils = {
    getIntegrationIdentifiers,
    showConfirmationDialog,
    selectIntegrationRun,
    hasAccessToRunIntegration,
    getConfirmationDialogSettings,
    getClientNames
};
